import {
  CARD_TITLE,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

const getSupportedApis = (): { label: string; supported: boolean }[] => [
  {
    label: 'RTCPeerConnection',
    supported: typeof RTCPeerConnection !== 'undefined',
  },
  {
    label: 'MediaDevices API',
    supported: !!navigator.mediaDevices,
  },
  {
    label: 'MediaStream',
    supported: typeof MediaStream !== 'undefined',
  },
  {
    label: 'AudioContext',
    supported:
      typeof AudioContext !== 'undefined' ||
      typeof (window as unknown as { webkitAudioContext: unknown })
        .webkitAudioContext !== 'undefined',
  },
];

/**
 * WebRTCSupportPanel - Which WebRTC browser APIs this runtime exposes
 *
 * Read at render time rather than module load: in Electron the renderer can
 * be reloaded with different flags, and a stale snapshot would be
 * misleading.
 */
export const WebRTCSupportPanel: React.FC = () => {
  const apis = getSupportedApis();
  const supportedCount = apis.filter(({ supported }) => supported).length;
  const isFullySupported = supportedCount === apis.length;

  return (
    <div className={SURFACE.card}>
      <div className={cn('flex items-center gap-2', SURFACE.cardHeader)}>
        <span className={CARD_TITLE}>Browser capability</span>
        <div className="flex-1" />
        <span
          className={cn(
            'text-[10px] font-semibold uppercase tracking-[0.06em]',
            isFullySupported ? TONE.ok.text : TONE.bad.text
          )}
        >
          {supportedCount} / {apis.length} supported
        </span>
      </div>

      <div className="grid grid-cols-2 gap-1.5 p-3">
        {apis.map(({ label, supported }) => {
          const tone = supported ? TONE.ok : TONE.bad;

          return (
            <div
              key={label}
              className={cn('flex items-center gap-2 px-[9px] py-2', tone.inset)}
            >
              <span className={cn('text-[11px]', tone.text)}>
                {supported ? '✓' : '✕'}
              </span>
              <span className="min-w-0 truncate font-sp-mono text-[11px] text-zinc-200">
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
